/**
 * Debug overlay for the layout. Rendered inside a space's coordinate system,
 * so it transforms with zoom/pan naturally.
 *
 * What it shows (for one space):
 *   • Per-node:
 *     – degree badges (in/out) at top-left of the node
 *     – "net force" vector arrow from node centre — where the solver would
 *       push this node right now
 *     – "ideal" ghost position (dashed outline) + dotted line showing drift
 *       from current position
 *     – team/domain chip at bottom-left
 *   • Per-edge:
 *     – midpoint label "current / ideal − stretch%"
 *     – color-coded ("green" at ideal, "red" overstretched, "blue" compressed)
 *     – flow-violation arrow (⇐) on edges where target is left of source
 *   • Per-pair:
 *     – faint red lines between close unconnected nodes, thickness scaled
 *       by repulsion force
 *   • Summary pill (bottom-right of the space) listing node/edge counts,
 *     mean/max stretch, flow violations, overlap count.
 */
import { memo } from "react";
import type { BoardSpace } from "@archlang/scene/board";
import { svgFragmentUrl } from "../svgFragmentUrl";
import {
  diagnoseSpace,
  diagnoseNodeForces,
  stretchColor,
  type NodeDiag,
  type EdgeDiag,
  type PairDiag,
  type SpaceDiag,
  type NodeForceBreakdown,
  type NodeForceVector,
  type ForceType,
} from "@archlang/scene/board";

interface DebugOverlayProps {
  readonly space: BoardSpace;
  /** Effective pixels-per-unit for this space; used to keep labels
   *  readable when zoomed out (we clamp label font sizes in screen px). */
  readonly scale: number;
  readonly selectedId: string | null;
}

export const DebugOverlay = memo(function DebugOverlay({
  space,
  scale,
  selectedId,
}: DebugOverlayProps) {
  const diag = diagnoseSpace(space);
  const selectedForces = selectedId ? diagnoseNodeForces(space, selectedId) : null;
  return (
    <>
      <ForceAndEdgeSvg diag={diag} scale={scale} spaceW={space.width} spaceH={space.height} />
      <NodeAnnotations diag={diag} scale={scale} />
      {selectedForces && (
        <>
          <SelectedForcesSvg
            breakdown={selectedForces}
            scale={scale}
            spaceW={space.width}
            spaceH={space.height}
          />
          <SelectedForcesPanel breakdown={selectedForces} scale={scale} />
        </>
      )}
      <SummaryPill diag={diag} scale={scale} spaceW={space.width} spaceH={space.height} />
    </>
  );
});

// ---- SVG: repulsion pairs + edge labels + force arrows ---------------------

function ForceAndEdgeSvg({
  diag,
  scale,
  spaceW,
  spaceH,
}: {
  diag: SpaceDiag;
  scale: number;
  spaceW: number;
  spaceH: number;
}) {
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${spaceW} ${spaceH}`}
      preserveAspectRatio="none"
      width={spaceW}
      height={spaceH}
      style={{ overflow: "visible" }}
    >
      <defs>
        <marker
          id="dbg-force"
          viewBox="0 0 10 10"
          refX="8"
          refY="5"
          markerWidth="10"
          markerHeight="10"
          orient="auto-start-reverse"
          markerUnits="userSpaceOnUse"
        >
          <path d="M 0 0 L 10 5 L 0 10 Z" fill="#fbbf24" />
        </marker>
        <marker
          id="dbg-flow-violation"
          viewBox="0 0 10 10"
          refX="5"
          refY="5"
          markerWidth="12"
          markerHeight="12"
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <circle cx="5" cy="5" r="4" fill="#ef4444" />
        </marker>
      </defs>

      {/* Faint repulsion lines for currently "hot" pairs. Thickness scales
          with repulsion magnitude so you can see which pairs are fighting. */}
      <HotPairLines hot={diag.hotPairs} nodes={diag.items} />

      {/* Edge stretch readouts + flow-violation markers. */}
      <EdgeReadouts edges={diag.edges} nodes={diag.items} scale={scale} />

      {/* Per-node force arrows + ideal-position ghosts. */}
      <NodeForceVectors nodes={diag.items} scale={scale} />
    </svg>
  );
}

function HotPairLines({
  hot,
  nodes,
}: {
  hot: readonly PairDiag[];
  nodes: readonly NodeDiag[];
}) {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  return (
    <g>
      {hot.map((p, i) => {
        const a = byId.get(p.idA);
        const b = byId.get(p.idB);
        if (!a || !b) return null;
        // Thickness: log-scaled by repulsion strength.
        const strokeW = Math.max(0.8, Math.min(5, Math.log10(p.repulsion + 1) - 1.2));
        const opacity = Math.min(0.5, 0.15 + p.repulsion / 2000);
        return (
          <line
            key={`pair-${i}`}
            x1={a.cx}
            y1={a.cy}
            x2={b.cx}
            y2={b.cy}
            stroke="#ef4444"
            strokeOpacity={opacity}
            strokeWidth={strokeW}
            strokeDasharray="2 6"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
    </g>
  );
}

function EdgeReadouts({
  edges,
  nodes,
  scale,
}: {
  edges: readonly EdgeDiag[];
  nodes: readonly NodeDiag[];
  scale: number;
}) {
  const byId = new Map(nodes.map((n) => [n.id, n] as const));
  // Font: want roughly 11px on screen, so work back from scale.
  const fontSpaceUnits = Math.max(10, 11 / Math.max(0.25, scale));
  return (
    <g>
      {edges.map((e) => {
        const a = byId.get(e.fromId);
        const b = byId.get(e.toId);
        if (!a || !b) return null;
        const mx = (a.cx + b.cx) / 2;
        const my = (a.cy + b.cy) / 2;
        const color = stretchColor(e.stretchPct);
        const boxW = fontSpaceUnits * 7.5;
        const boxH = fontSpaceUnits * 2.2;
        return (
          <g key={`edge-${e.id}`}>
            {/* Label backdrop */}
            <rect
              x={mx - boxW / 2}
              y={my - boxH / 2}
              width={boxW}
              height={boxH}
              rx={fontSpaceUnits * 0.4}
              fill="#0f172a"
              fillOpacity={0.85}
              stroke={color}
              strokeOpacity={0.8}
              strokeWidth={1}
              vectorEffect="non-scaling-stroke"
            />
            <text
              x={mx}
              y={my - fontSpaceUnits * 0.2}
              textAnchor="middle"
              fontSize={fontSpaceUnits * 0.85}
              fill={color}
              fontWeight={700}
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              style={{ userSelect: "none" }}
            >
              {Math.round(e.edgeGap)}/{e.idealLen}
            </text>
            <text
              x={mx}
              y={my + fontSpaceUnits * 0.8}
              textAnchor="middle"
              fontSize={fontSpaceUnits * 0.75}
              fill="#e2e8f0"
              fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
              style={{ userSelect: "none" }}
            >
              {e.stretchPct >= 0 ? "+" : ""}
              {e.stretchPct.toFixed(0)}% · k={e.stiffness.toFixed(2)} · d{e.minDegree}
              {e.sameTeam ? " · team" : ""}
            </text>
            {e.flowViolation && (
              <circle
                cx={mx}
                cy={my - boxH / 2 - fontSpaceUnits * 0.6}
                r={fontSpaceUnits * 0.5}
                fill="#ef4444"
                stroke="#fecaca"
                strokeWidth={1}
                vectorEffect="non-scaling-stroke"
              />
            )}
          </g>
        );
      })}
    </g>
  );
}

function NodeForceVectors({
  nodes,
  scale,
}: {
  nodes: readonly NodeDiag[];
  scale: number;
}) {
  // Force arrow magnitude: gradient descent would move the node in the
  // opposite direction of `netForce` (gradient). Scale arrow length by
  // log magnitude so all force magnitudes are visible.
  const lineWidth = Math.max(1.2, 2.5 / Math.max(0.25, scale));
  return (
    <g>
      {nodes.map((n) => {
        const mag = n.netForceMag;
        if (mag < 0.01) return null;
        // Visual length proportional to log of magnitude, clamped.
        const length = Math.max(30, Math.min(220, 50 + 35 * Math.log10(1 + mag)));
        // netForce is stored as FORCE (direction the node would move), so
        // the arrow just uses it directly.
        const ux = n.netForce.x / mag;
        const uy = n.netForce.y / mag;
        const tx = n.cx + ux * length;
        const ty = n.cy + uy * length;
        // Dashed line from current centre → ideal centre (ghost destination).
        const ix = n.idealCx;
        const iy = n.idealCy;
        return (
          <g key={`force-${n.id}`}>
            <line
              x1={n.cx}
              y1={n.cy}
              x2={tx}
              y2={ty}
              stroke="#fbbf24"
              strokeOpacity={0.9}
              strokeWidth={lineWidth}
              markerEnd={svgFragmentUrl("dbg-force")}
              vectorEffect="non-scaling-stroke"
            />
            {/* Ghost rectangle at ideal position. */}
            <rect
              x={ix - n.halfW}
              y={iy - n.halfH}
              width={n.halfW * 2}
              height={n.halfH * 2}
              fill="none"
              stroke="#38bdf8"
              strokeOpacity={0.6}
              strokeWidth={1}
              strokeDasharray="8 5"
              vectorEffect="non-scaling-stroke"
              rx={8}
            />
            <line
              x1={n.cx}
              y1={n.cy}
              x2={ix}
              y2={iy}
              stroke="#38bdf8"
              strokeOpacity={0.35}
              strokeWidth={1}
              strokeDasharray="3 4"
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })}
    </g>
  );
}

// ---- DOM: per-node pills (degree + team) ----------------------------------

function NodeAnnotations({
  diag,
  scale,
}: {
  diag: SpaceDiag;
  scale: number;
}) {
  // Font in space units so DOM text scales with zoom like everything else.
  const pillH = Math.max(18, 20 / Math.max(0.3, scale));
  const fontPx = pillH * 0.65;
  return (
    <div className="absolute inset-0 pointer-events-none">
      {diag.items.map((n) => (
        <div
          key={`anno-${n.id}`}
          className="absolute"
          style={{
            left: n.cx - n.halfW,
            top: n.cy - n.halfH,
            width: n.halfW * 2,
            height: n.halfH * 2,
          }}
        >
          {/* Degree badge: top-left corner, above the node */}
          <div
            className="absolute flex items-center gap-1 rounded-md bg-slate-950/(--tint-solid) border border-amber-400/80 text-amber-200 font-mono font-semibold"
            style={{
              top: -pillH - 2,
              left: 0,
              height: pillH,
              padding: `0 ${pillH * 0.35}px`,
              fontSize: fontPx,
              lineHeight: 1,
            }}
          >
            <span style={{ color: "#fbbf24" }}>deg {n.degree}</span>
            <span className="text-slate-400">·</span>
            <span>in {n.inDegree}</span>
            <span className="text-slate-400">·</span>
            <span>out {n.outDegree}</span>
          </div>

          {/* Team / domain chip: bottom-left, below the node */}
          {(n.team || n.domain) && (
            <div
              className="absolute rounded-sm bg-slate-950/90 border border-slate-500 text-slate-300 font-mono"
              style={{
                top: n.halfH * 2 + 2,
                left: 0,
                height: pillH,
                padding: `0 ${pillH * 0.35}px`,
                fontSize: fontPx * 0.85,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                gap: pillH * 0.2,
              }}
            >
              {n.team && (
                <>
                  <span className="text-emerald-400">T</span>
                  <span>{n.team}</span>
                </>
              )}
              {n.domain && (
                <>
                  {n.team && <span className="text-slate-500">·</span>}
                  <span className="text-sky-400">D</span>
                  <span>{n.domain}</span>
                </>
              )}
            </div>
          )}

          {/* Force-magnitude readout: top-right corner */}
          {n.netForceMag > 0.5 && (
            <div
              className="absolute rounded-sm bg-slate-950/(--tint-solid) border border-amber-400 text-amber-200 font-mono"
              style={{
                top: -pillH - 2,
                right: 0,
                height: pillH,
                padding: `0 ${pillH * 0.35}px`,
                fontSize: fontPx * 0.85,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
              }}
            >
              F {n.netForceMag.toFixed(1)}
            </div>
          )}

          {/* Closest-neighbour readout: bottom-right */}
          <div
            className="absolute rounded-sm bg-slate-950/90 border border-slate-600 text-slate-400 font-mono"
            style={{
              top: n.halfH * 2 + 2,
              right: 0,
              height: pillH,
              padding: `0 ${pillH * 0.3}px`,
              fontSize: fontPx * 0.8,
              lineHeight: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            ↔ {Math.round(n.closestDist)}
          </div>
        </div>
      ))}
    </div>
  );
}

// ---- Selected-node per-force breakdown -------------------------------------

const FORCE_COLORS: Record<ForceType, string> = {
  spring: "#4ade80",     // green — attraction
  repulsion: "#f87171",  // red — push away
  flow: "#38bdf8",       // sky blue — horizontal rank bias
  alignment: "#a78bfa",  // purple — axis snap
  port: "#fbbf24",       // amber — external boundary
  parent: "#94a3b8",     // slate — gravity
};

function SelectedForcesSvg({
  breakdown,
  scale,
  spaceW,
  spaceH,
}: {
  breakdown: NodeForceBreakdown;
  scale: number;
  spaceW: number;
  spaceH: number;
}) {
  // Log-scale arrow length so 1-unit and 1000-unit forces are both visible.
  const arrowLen = (mag: number) =>
    Math.max(28, Math.min(520, 42 + 70 * Math.log10(1 + mag)));
  const lineW = Math.max(1.5, 3 / Math.max(0.25, scale));
  const fontPx = Math.max(10, 12 / Math.max(0.3, scale));

  // Compute screen-space layout of labels: place each label at the arrow
  // tip, nudged radially outward so it sits clear of the node.
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      viewBox={`0 0 ${spaceW} ${spaceH}`}
      preserveAspectRatio="none"
      width={spaceW}
      height={spaceH}
      style={{ overflow: "visible" }}
    >
      <defs>
        {(Object.keys(FORCE_COLORS) as ForceType[]).map((t) => (
          <marker
            key={t}
            id={`sel-arrow-${t}`}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="11"
            markerHeight="11"
            orient="auto-start-reverse"
            markerUnits="userSpaceOnUse"
          >
            <path d="M 0 0 L 10 5 L 0 10 Z" fill={FORCE_COLORS[t]} />
          </marker>
        ))}
      </defs>

      {/* Hit ellipse — the silhouette the solver actually uses for
          ellipse edge-distance math. Dashed so you can compare it against
          the rectangle rendered by the node. */}
      <ellipse
        cx={breakdown.cx}
        cy={breakdown.cy}
        rx={breakdown.halfW}
        ry={breakdown.halfH}
        fill="none"
        stroke="#fbbf24"
        strokeOpacity={0.75}
        strokeWidth={2}
        strokeDasharray="6 4"
        vectorEffect="non-scaling-stroke"
      />

      {/* Force arrows */}
      {breakdown.forces.map((f, i) => {
        if (f.magnitude < 0.1) return null;
        const len = arrowLen(f.magnitude);
        const ux = f.fx / f.magnitude;
        const uy = f.fy / f.magnitude;
        const tx = breakdown.cx + ux * len;
        const ty = breakdown.cy + uy * len;
        const color = FORCE_COLORS[f.type];
        return (
          <g key={`force-${i}`}>
            {/* Optional peer guideline — only for springs (to show which
                node the attraction targets) and repulsions */}
            {(f.type === "spring" || f.type === "repulsion") &&
              f.peerCx !== undefined &&
              f.peerCy !== undefined && (
                <line
                  x1={breakdown.cx}
                  y1={breakdown.cy}
                  x2={f.peerCx}
                  y2={f.peerCy}
                  stroke={color}
                  strokeOpacity={0.25}
                  strokeWidth={1}
                  strokeDasharray="2 4"
                  vectorEffect="non-scaling-stroke"
                />
              )}
            <line
              x1={breakdown.cx}
              y1={breakdown.cy}
              x2={tx}
              y2={ty}
              stroke={color}
              strokeWidth={lineW}
              markerEnd={svgFragmentUrl(`sel-arrow-${f.type}`)}
              vectorEffect="non-scaling-stroke"
            />
            {/* Label at tip */}
            <g transform={`translate(${tx + ux * 8}, ${ty + uy * 8})`}>
              <rect
                x={-2}
                y={-fontPx * 0.9}
                width={f.label.length * fontPx * 0.55 + 6}
                height={fontPx * 1.3}
                fill="#0f172a"
                fillOpacity={0.9}
                stroke={color}
                strokeOpacity={0.6}
                strokeWidth={1}
                rx={3}
                vectorEffect="non-scaling-stroke"
              />
              <text
                x={2}
                y={0}
                fontSize={fontPx * 0.85}
                fill={color}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fontWeight={700}
                style={{ userSelect: "none" }}
              >
                {f.label} · {f.magnitude.toFixed(1)}
              </text>
            </g>
          </g>
        );
      })}

      {/* Net-force vector in bright yellow on top */}
      {breakdown.netMag > 0.5 && (() => {
        const len = arrowLen(breakdown.netMag);
        const ux = breakdown.netFx / breakdown.netMag;
        const uy = breakdown.netFy / breakdown.netMag;
        const tx = breakdown.cx + ux * len;
        const ty = breakdown.cy + uy * len;
        return (
          <g>
            <line
              x1={breakdown.cx}
              y1={breakdown.cy}
              x2={tx}
              y2={ty}
              stroke="#facc15"
              strokeWidth={lineW * 1.8}
              strokeDasharray="10 4"
              vectorEffect="non-scaling-stroke"
            />
            <circle
              cx={tx}
              cy={ty}
              r={Math.max(4, lineW * 2.5)}
              fill="#facc15"
              stroke="#0f172a"
              strokeWidth={1.5}
              vectorEffect="non-scaling-stroke"
            />
          </g>
        );
      })()}
    </svg>
  );
}

function SelectedForcesPanel({
  breakdown,
  scale,
}: {
  breakdown: NodeForceBreakdown;
  scale: number;
}) {
  // Panel in space coords so it zooms with the canvas. Positioned to the
  // right of the selected node at a small offset.
  const baseFont = Math.max(11, 13 / Math.max(0.3, scale));
  // Sum by type.
  const byType = new Map<ForceType, number>();
  for (const f of breakdown.forces) {
    byType.set(f.type, (byType.get(f.type) ?? 0) + f.magnitude);
  }
  const panelX = breakdown.cx + breakdown.halfW + 40;
  const panelY = breakdown.cy - breakdown.halfH - 20;
  return (
    <div
      className="absolute rounded-lg bg-slate-950/(--tint-solid) border border-amber-500 text-slate-200 font-mono shadow-2xl"
      style={{
        left: panelX,
        top: panelY,
        padding: baseFont * 0.7,
        fontSize: baseFont,
        lineHeight: 1.5,
        minWidth: baseFont * 28,
        maxWidth: baseFont * 40,
        pointerEvents: "auto",
      }}
    >
      <div className="text-amber-300 font-bold mb-1 truncate">
        forces on {breakdown.label}
      </div>
      <div className="text-slate-400 text-xs mb-2">
        net · ({breakdown.netFx.toFixed(1)}, {breakdown.netFy.toFixed(1)}){" "}
        · |F|={breakdown.netMag.toFixed(1)}
      </div>
      {/* Type totals */}
      <div className="mb-2 grid grid-cols-2 gap-x-3 gap-y-0.5">
        {(["spring", "repulsion", "flow", "alignment", "port", "parent"] as ForceType[]).map(
          (t) => {
            const sum = byType.get(t);
            if (sum === undefined) return null;
            return (
              <div
                key={t}
                className="flex items-center gap-1.5 text-xs whitespace-nowrap"
              >
                <span
                  style={{
                    width: baseFont * 0.6,
                    height: baseFont * 0.6,
                    background: FORCE_COLORS[t],
                    display: "inline-block",
                    borderRadius: 2,
                  }}
                />
                <span className="text-slate-300">{t}</span>
                <span className="ml-auto text-slate-400">{sum.toFixed(1)}</span>
              </div>
            );
          },
        )}
      </div>
      {/* Detailed list */}
      <div className="border-t border-slate-700 pt-1">
        {breakdown.forces.slice(0, 14).map((f, i) => (
          <div key={i} className="flex items-center gap-1.5 text-xs whitespace-nowrap">
            <span
              style={{
                width: baseFont * 0.5,
                height: baseFont * 0.5,
                background: FORCE_COLORS[f.type],
                display: "inline-block",
                borderRadius: 2,
              }}
            />
            <span className="flex-1 truncate text-slate-300" title={f.label}>
              {f.label}
            </span>
            <span className="tabular-nums text-slate-400">
              {f.magnitude.toFixed(1)}
            </span>
          </div>
        ))}
        {breakdown.forces.length > 14 && (
          <div className="text-slate-500 text-xs mt-1">
            … and {breakdown.forces.length - 14} smaller
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Summary pill ---------------------------------------------------------

function SummaryPill({
  diag,
  scale,
  spaceW,
  spaceH,
}: {
  diag: SpaceDiag;
  scale: number;
  spaceW: number;
  spaceH: number;
}) {
  const s = diag.summary;
  const baseFont = Math.max(11, 13 / Math.max(0.3, scale));
  return (
    <div
      className="absolute rounded-lg bg-slate-950/(--tint-solid) border border-slate-600 text-slate-200 font-mono shadow-lg"
      style={{
        left: spaceW - 360,
        top: spaceH - 150,
        padding: baseFont * 0.6,
        fontSize: baseFont,
        lineHeight: 1.4,
        pointerEvents: "auto",
      }}
    >
      <div className="text-amber-300 font-bold mb-1">layout diagnostics</div>
      <div>nodes: {s.nodeCount} · edges: {s.edgeCount}</div>
      <div>
        stretch · avg {s.avgStretchPct.toFixed(1)}% · max{" "}
        <span style={{ color: stretchColor(s.maxStretchPct), fontWeight: 700 }}>
          {s.maxStretchPct.toFixed(1)}%
        </span>
      </div>
      <div>
        flow violations:{" "}
        <span className={s.flowViolations > 0 ? "text-red-400" : "text-emerald-400"}>
          {s.flowViolations}
        </span>
      </div>
      <div>
        overlapping pairs:{" "}
        <span className={s.overlappingPairs > 0 ? "text-red-400" : "text-emerald-400"}>
          {s.overlappingPairs}
        </span>
      </div>
      <div className="text-slate-500 text-xs mt-1">
        arrows: net force · ghosts: ideal position · red lines: repulsion
      </div>
    </div>
  );
}
